import { useCallback, useEffect, useMemo, useState } from 'react';
import Confetti from 'react-confetti';
import { useSearchParams } from 'react-router-dom';
import { ACTIVE_TAB_PARAM, CONFETTI_PARAM } from '../../constants';
import { useGetFiltersFromParams, useTimer } from '../../hooks';
import { SimulationsFilters, SimulationsTab } from '../../types';
import { isFiltersDirty } from '../../utils';
import SimulationsExplorer from './SimulationsExplorer';
import SimulationsPageHeader from './SimulationsPageHeader';
import SimulationsTableView from './SimulationsTableView';

const CONFETTI_GRAVITY = 0.8;
const CONFETTI_DURATION = 2500;
const CONFETTI_NUMBER_OF_PIECES = 400;

const SimulationsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get(ACTIVE_TAB_PARAM) as SimulationsTab;
  const confettiParam = searchParams.get(CONFETTI_PARAM);

  const [isLoading, setIsLoading] = useState(false);
  const filters: SimulationsFilters = useGetFiltersFromParams();

  const filtersApplied = useMemo(() => isFiltersDirty(filters), [filters]);

  // Show the table only if the user is not in the library tab, or if there are any filters applied.
  const showTableView = activeTab !== SimulationsTab.LIBRARY || filtersApplied;

  const shouldShowConfetti = confettiParam === 'true' && !isLoading;

  const handleConfettiTimerComplete = useCallback(() => {
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.delete(CONFETTI_PARAM);
    setSearchParams(newSearchParams);
  }, [searchParams, setSearchParams]);

  const { startTimer, stopTimer } = useTimer({
    callback: handleConfettiTimerComplete,
    delay: CONFETTI_DURATION,
  });

  //  If confetti is being shown, automatically hide it after duration.
  useEffect(() => {
    if (shouldShowConfetti) {
      startTimer();
    }
    return () => {
      stopTimer();
    };
  }, [shouldShowConfetti, startTimer, stopTimer]);

  return (
    <div className="flex h-full flex-col">
      {shouldShowConfetti && (
        <Confetti
          gravity={CONFETTI_GRAVITY}
          numberOfPieces={CONFETTI_NUMBER_OF_PIECES}
          className="fixed left-0 top-0 !z-10"
        />
      )}
      <div className="flex py-4 pl-8 pr-4">
        <SimulationsPageHeader filterValue={filters.search || ''} />
      </div>
      {showTableView && (
        <SimulationsTableView
          activeTab={activeTab}
          filters={filters}
          filtersApplied={filtersApplied}
          setIsLoading={setIsLoading}
        />
      )}
      {!showTableView && <SimulationsExplorer setIsLoading={setIsLoading} />}
    </div>
  );
};

export default SimulationsPage;
